<template>
  <b-card class="border-0 shadow-sm mt-4">
    <template #header>
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
        <div>
          <h3 class="h5 mb-1 text-primary">Planes de membresía</h3>
          <p class="mb-0 small text-muted">Afiliados y comisiones de tus agentes de planes. Sin fechas se muestra el mes en curso.</p>
        </div>
      </div>
    </template>

    <b-form @submit.prevent="applyRange">
      <b-row class="align-items-end">
        <b-col cols="12" md="3" class="mb-3">
          <label class="font-weight-semibold">Desde</label>
          <flat-pickr v-model="filters.start_date" :config="dateConfig" class="form-control" placeholder="YYYY-MM-DD" />
        </b-col>
        <b-col cols="12" md="3" class="mb-3">
          <label class="font-weight-semibold">Hasta</label>
          <flat-pickr v-model="filters.end_date" :config="dateConfig" class="form-control" placeholder="YYYY-MM-DD" />
        </b-col>
        <b-col cols="12" md="3" class="mb-3">
          <b-button type="submit" variant="primary" :disabled="loading">
            <span v-if="!loading">Buscar</span>
            <b-spinner v-else small label="Loading..."></b-spinner>
          </b-button>
        </b-col>
      </b-row>
    </b-form>

    <b-alert v-if="errorMessage" show variant="danger" dismissible @dismissed="errorMessage = null">
      {{ errorMessage }}
    </b-alert>

    <b-spinner v-if="loading && !dashboard" label="Loading..." class="d-block mx-auto my-4"></b-spinner>

    <template v-if="dashboard">
      <p class="small text-muted mb-2">
        {{ totals.agents || 0 }} agentes · comisiones de los agentes: estimada del mes {{ money(agentsCommissions.estimated_current_month) }},
        por cobrar {{ money(agentsCommissions.pending) }}, cobrado {{ money(agentsCommissions.paid_total) }}.
      </p>
      <agent-summary-cards :summary="summaryCards" commissions-title="Comisión de la agencia" />

      <b-tabs card class="mt-2">
        <b-tab title="Agentes" active>
          <b-table
            :items="agents"
            :fields="agentFields"
            :busy="loading"
            responsive
            hover
            show-empty
            empty-text="Sin agentes de planes."
          >
            <template #cell(agent)="{ item }">{{ (item.agent && item.agent.name) || '—' }}</template>
            <template #cell(active)="{ item }">
              {{ item.active.members }}
              <div class="small text-muted">{{ item.active.families }} familias · {{ item.active.companies }} empresas</div>
            </template>
            <template #cell(new)="{ item }">{{ item.period.new.members }}</template>
            <template #cell(attention)="{ item }">
              <span :class="{ 'text-danger font-weight-bold': attentionOf(item) > 0 }">{{ attentionOf(item) }}</span>
              <div class="small text-muted">{{ item.attention.payment_failed }} pago fallido · {{ item.attention.cancel_scheduled }} cancelación</div>
            </template>
            <template #cell(commissions)="{ item }">
              {{ money(item.commissions.estimated_current_month) }}
              <div class="small text-muted">Por cobrar {{ money(item.commissions.pending.total) }}</div>
            </template>
            <template #cell(patient_flow)="{ item }">
              {{ money(item.patient_flow.amount) }}
              <div class="small text-muted">{{ item.patient_flow.generated }} de {{ item.patient_flow.attended }} citas</div>
            </template>
          </b-table>
          <PaginationTable
            :dataMeta="agentsMeta"
            :totalUsers="agentsTotal"
            :perPage="perPage"
            :currentPage="currentPage"
            @page-changed="load"
          />
        </b-tab>
        <b-tab title="Comisión de la agencia por mes">
          <commission-months-table agency />
        </b-tab>
      </b-tabs>
    </template>
  </b-card>
</template>

<script>
import { BAlert, BButton, BCard, BCol, BForm, BRow, BSpinner, BTab, BTable, BTabs } from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import agentPlanService from '@/core/services/api/agentPlan'
import PaginationTable from '@/components/PaginationTable'
import { formatYmd } from '@/core/utils/dashboardDates'
import { money, rangeParams, unwrapPage } from '@/core/utils/agentPlanDashboard'
import { membershipApiMessage } from '@/core/utils/membershipAdmin'
import AgentSummaryCards from './AgentSummaryCards.vue'
import CommissionMonthsTable from './CommissionMonthsTable.vue'

/** Panel de planes de la agencia (rol agent admin): totales de sus agentes, Élite de agencia y comisión propia. */
export default {
  name: 'AgencyPlanPanel',
  components: {
    BAlert,
    BButton,
    BCard,
    BCol,
    BForm,
    BRow,
    BSpinner,
    BTab,
    BTable,
    BTabs,
    flatPickr,
    PaginationTable,
    AgentSummaryCards,
    CommissionMonthsTable,
  },
  data() {
    const today = new Date()
    const filters = {
      start_date: formatYmd(new Date(today.getFullYear(), today.getMonth(), 1)),
      end_date: formatYmd(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    }
    return {
      loading: false,
      errorMessage: null,
      dashboard: null,
      agents: [],
      agentsTotal: 0,
      agentsMeta: { from: 0, to: 0, of: 0 },
      perPage: 20,
      currentPage: { page: 1 },
      requestSeq: 0,
      filters,
      range: { ...filters },
      dateConfig: {
        dateFormat: 'Y-m-d',
        allowInput: true,
      },
      agentFields: [
        { key: 'agent', label: 'Agente' },
        { key: 'active', label: 'Miembros activos' },
        { key: 'new', label: 'Nuevos' },
        { key: 'attention', label: 'Requieren atención' },
        { key: 'commissions', label: 'Comisión estimada' },
        { key: 'patient_flow', label: 'PatientFlow' },
      ],
    }
  },
  computed: {
    totals() {
      return (this.dashboard && this.dashboard.totals) || {}
    },
    agentsCommissions() {
      return this.totals.agents_commissions || {}
    },
    summaryCards() {
      return {
        active: this.totals.active,
        period: this.totals.period,
        attention: this.totals.attention,
        patient_flow: this.totals.patient_flow,
        elite: this.dashboard && this.dashboard.elite,
        commissions: this.dashboard && this.dashboard.agency_commissions,
      }
    },
  },
  mounted() {
    this.load(1)
  },
  methods: {
    money,
    attentionOf(item) {
      return (Number(item.attention.payment_failed) || 0) + (Number(item.attention.cancel_scheduled) || 0)
    },
    applyRange() {
      const { start_date: start, end_date: end } = this.filters
      // El backend pide las dos fechas o ninguna (mes en curso).
      if (!start !== !end) {
        this.errorMessage = 'Elige las dos fechas o deja ambas vacías para ver el mes en curso.'
        return
      }
      if (start && end && start > end) {
        this.errorMessage = 'La fecha inicial debe ser anterior o igual a la final.'
        return
      }
      this.range = { ...this.filters }
      this.load(1)
    },
    async load(page = this.currentPage.page) {
      const seq = ++this.requestSeq
      this.loading = true
      this.errorMessage = null
      try {
        const response = await agentPlanService.getAgencyDashboard({ ...rangeParams(this.range), per_page: this.perPage, page })
        if (seq !== this.requestSeq) return
        const data = (response && response.data) || null
        const agents = unwrapPage(data && data.agents)
        this.dashboard = data
        this.agents = agents.items
        this.agentsTotal = agents.total
        this.agentsMeta = { from: agents.from, to: agents.to, of: agents.total }
        this.currentPage.page = agents.page
      } catch (error) {
        if (seq !== this.requestSeq) return
        this.dashboard = null
        this.errorMessage = membershipApiMessage(error, 'No se pudo cargar el panel de la agencia. Intenta de nuevo.')
      } finally {
        if (seq === this.requestSeq) this.loading = false
      }
    },
  },
}
</script>
